import React, { useState, useEffect, useMemo } from 'react';
import {
	Box,
	Button,
	Container,
	Grid,
	Paper,
	TextField,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Checkbox,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import i18next from '../../lang';
import {Product} from "./Product/Interfaces";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { IconButton, Stack, InputAdornment, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { adminStyles } from '../../theme/adminStyles';

// interface Product {
// 	id: number;
// 	category: {
// 		name: string;
// 	};
// 	curriculumName: string;
// 	schoolType: string;
// 	targetGrade: string;
// 	volume: string;
// 	title: string;
// 	subTitle: string;
// 	price: number;
// 	status: number;
// }

interface ProductSetFormData {
	name: string;
	description: string;
	price: string;
	productIds: number[];
}

interface ProductSetFilterState {
	keyword: string;
}

const ProductSetForm: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [products, setProducts] = useState<Product[]>([]);
	const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
	const [filters, setFilters] = useState<ProductSetFilterState>({
		keyword: ''
	});

	const [formData, setFormData] = useState<ProductSetFormData>({
		name: '',
		description: '',
		price: '',
		productIds: [],
	});

	// テーブル本文と統一するフォントサイズ
	const fontSize = '0.8rem';

	// 保存バリデーション: セット名・価格入力必須、商品1件以上選択
	const canSave = formData.name.trim() !== '' &&
		formData.price !== '' &&
		formData.productIds.length > 0;

	useEffect(() => {
		fetchProducts();
		if (id) {
			fetchProductSet();
		}
	}, [id]);

	const fetchProducts = async () => {
		try {
			const response = await axios.get('/api/v1/admin/products');

			setProducts(response.data.filter(p => p.status === 'ACTIVE'));
			setFilteredProducts(response.data.filter(p => p.status === 'ACTIVE'));
		} catch (error) {
			console.error('Failed to fetch products:', error);
		}
	};

	const handleSearch = () => {
		const filtered = products.filter(product => {
			const matchesKeyword = !filters.keyword ||
				(product.curriculumName && product.curriculumName.toLowerCase().includes(filters.keyword.toLowerCase())) ||
				(product.volume && product.volume.toLowerCase().includes(filters.keyword.toLowerCase())) ||
				(product.title && product.title.toLowerCase().includes(filters.keyword.toLowerCase())) ||
				(product.code && product.code.toLowerCase().includes(filters.keyword.toLowerCase()))

			return matchesKeyword;
		});

		setFilteredProducts(filtered);
	};

	const handleFilterChange = (field: keyof ProductSetFilterState, value: string) => {
		setFilters(prev => ({
			...prev,
			[field]: value
		}));
	};

	const fetchProductSet = async () => {
		try {
			const response = await axios.get(`/api/v1/admin/product-sets/${id}`);
			const productSet = response.data;
			setFormData({
				name: productSet.name,
				description: productSet.description || '',
				price: productSet.price.toString(),
				productIds: productSet.products.map((p: Product) => p.id),
			});
		} catch (error) {
			console.error('Failed to fetch product set:', error);
		}
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		const price = parseFloat(formData.price);
		if (isNaN(price) || price < 0) {
			alert(i18next.t('product.price_must_be_zero_or_positive'));
			return;
		}

		const productSetData = {
			...formData,
			price,
		};

		try {
			if (id) {
				await axios.put(`/api/v1/admin/product-sets/${id}`, productSetData);
			} else {
				await axios.post('/api/v1/admin/product-sets', productSetData);
			}
			navigate('/admin/product-sets');
		} catch (error) {
			console.error('Failed to save product set:', error);
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const [collapsedCurriculums, setCollapsedCurriculums] = useState<Set<string>>(new Set());

	// カリキュラム名で商品をグループ化
	const groupedProducts = useMemo(() => {
		const groups: Record<string, Product[]> = {};
		filteredProducts.forEach(product => {
			const key = product.curriculumName || '未分類';
			if (!groups[key]) groups[key] = [];
			groups[key].push(product);
		});
		return groups;
	}, [filteredProducts]);

	const toggleCurriculumCollapse = (name: string) => {
		setCollapsedCurriculums(prev => {
			const next = new Set(prev);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			return next;
		});
	};

	const handleExpandAllCurriculums = () => setCollapsedCurriculums(new Set());
	const handleCollapseAllCurriculums = () => setCollapsedCurriculums(new Set(Object.keys(groupedProducts)));

	const getCurriculumSelectState = (rows: Product[]) => {
		const ids = rows.map(r => r.id);
		const selectedCount = ids.filter(id => formData.productIds.includes(id)).length;
		return {
			allSelected: selectedCount === ids.length && ids.length > 0,
			indeterminate: selectedCount > 0 && selectedCount < ids.length,
		};
	};

	const handleCurriculumSelect = (rows: Product[]) => {
		const ids = rows.map(r => r.id);
		const { allSelected } = getCurriculumSelectState(rows);
		setFormData(prev => {
			if (allSelected) {
				return { ...prev, productIds: prev.productIds.filter(id => !ids.includes(id)) };
			} else {
				const newIds = ids.filter(id => !prev.productIds.includes(id));
				return { ...prev, productIds: [...prev.productIds, ...newIds] };
			}
		});
	};

	const handleProductToggle = (productId: number) => {
		setFormData((prev) => {
			const newProductIds = prev.productIds.includes(productId)
				? prev.productIds.filter(id => id !== productId)
				: [...prev.productIds, productId];

			return {
				...prev,
				productIds: newProductIds,
			};
		});
	};

	return (
		<Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
			{/* ヘッダー */}
			<Box sx={adminStyles.headerBox}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<IconButton
						onClick={() => navigate('/admin/product-sets')}
						sx={{ bgcolor: '#f5f5f7', '&:hover': { bgcolor: '#e8e8e8' } }}
					>
						<ArrowBackIcon />
					</IconButton>
					</Box>
			</Box>

			<Paper sx={adminStyles.contentPaper}>
				<Box component="form" onSubmit={handleSubmit} onKeyDown={(e) => {
					// Enterキーによるフォームsubmitを防止（保存はボタンクリックのみ）
					if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
						e.preventDefault();
					}
				}}>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label={i18next.t('product_set.set_name')}
								name="name"
								value={formData.name}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label={i18next.t('common.description')}
								name="description"
								value={formData.description}
								onChange={handleChange}
								multiline
								rows={4}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label={i18next.t('product.price')}
								name="price"
								type="number"
								value={formData.price}
								onChange={handleChange}
								required
								inputProps={{ min: 0 }}
							/>
						</Grid>
						<Grid item xs={12}>
							<Typography variant="h6" sx={adminStyles.sectionTitle}>
								{i18next.t('product.select_product')}
							</Typography>
							<Paper sx={adminStyles.filterPaper}>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, '@media (min-width: 1080px)': { flexDirection: 'row', alignItems: 'center', gap: 2 } }}>
									{/* グループ1: フィルター + 検索 */}
									<Stack direction="row" spacing={1} alignItems="center" sx={{
										flex: 1, minWidth: 0,
									}}>
										<TextField
											variant="outlined"
											size="small"
											placeholder={i18next.t('product.search_hint_short')}
											value={filters.keyword}
											onChange={(e) => handleFilterChange('keyword', e.target.value)}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault();
													handleSearch();
												}
											}}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<SearchIcon sx={{ fontSize: '1.1rem' }} />
													</InputAdornment>
												),
											}}
											sx={{ flex: 1, '@media (min-width: 1080px)': { maxWidth: 370 }, '& .MuiInputBase-root': { height: 32, fontSize } }}
										/>
										<Button
											variant="contained"
											size="small"
											onClick={handleSearch}
											sx={{ ...adminStyles.searchButton, flexShrink: 0, fontSize, height: 32, minWidth: 'unset', px: 2 }}
										>
											{i18next.t('common.search')}
										</Button>
									</Stack>
									{/* 下段: グループ2 + 3 (狭い画面では1行に並ぶ) */}
									<Box sx={{
										display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, width: '100%',
										'@media (min-width: 1080px)': { ml: 'auto', flex: 'none', width: 'auto' },
									}}>
										{/* グループ2: 展開・閉じる */}
										<Stack direction="row" spacing={1}>
											<Tooltip title={i18next.t('common.expand_all')}>
												<Button
													variant="outlined"
													size="small"
													onClick={handleExpandAllCurriculums}
													sx={{ minWidth: 'unset', px: 0.5, height: 32 }}
												>
													<UnfoldMoreIcon fontSize="small" />
												</Button>
											</Tooltip>
											<Tooltip title={i18next.t('common.collapse_all')}>
												<Button
													variant="outlined"
													size="small"
													onClick={handleCollapseAllCurriculums}
													sx={{ minWidth: 'unset', px: 0.5, height: 32 }}
												>
													<UnfoldLessIcon fontSize="small" />
												</Button>
											</Tooltip>
										</Stack>
										{/* グループ3: キャンセル・保存 */}
										<Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
										<Button
											variant="outlined"
											size="small"
											onClick={() => navigate('/admin/product-sets')}
											sx={{ ...adminStyles.actionButton, flexShrink: 0, fontSize, height: 32 }}
										>
											{i18next.t('common.cancel')}
										</Button>
										<Button
											type="submit"
											variant="contained"
											color="primary"
											size="small"
											disabled={!canSave}
											sx={{ ...adminStyles.actionButton, flexShrink: 0, fontSize, height: 32 }}
										>
											{i18next.t('common.save')}
										</Button>
									</Stack>
									</Box>
								</Box>
							</Paper>
							<Paper sx={adminStyles.tablePaper}>
								<TableContainer sx={{ overflowX: 'auto' }}>
									<Table size="small" sx={{ minWidth: 650 }}>
										<TableHead>
											<TableRow sx={adminStyles.tableHeaderRow}>
												<TableCell padding="checkbox">
													<Checkbox
														checked={filteredProducts.length > 0 && formData.productIds.length === filteredProducts.length}
														indeterminate={formData.productIds.length > 0 && formData.productIds.length < filteredProducts.length}
														onChange={() => {
															if (formData.productIds.length === filteredProducts.length) {
																setFormData(prev => ({ ...prev, productIds: [] }));
															} else {
																setFormData(prev => ({ ...prev, productIds: filteredProducts.map(p => p.id) }));
															}
														}}
													/>
												</TableCell>
												<TableCell sx={{ ...adminStyles.tableHeaderCell, minWidth: 80 }}>{i18next.t('common.type_label')}</TableCell>
												<TableCell sx={{ ...adminStyles.tableHeaderCell, minWidth: 120 }}>{i18next.t('product.curriculum_name')}</TableCell>
												<TableCell sx={{ ...adminStyles.tableHeaderCell, minWidth: 100 }}>{i18next.t('product.volume')}</TableCell>
												<TableCell sx={{ ...adminStyles.tableHeaderCell, minWidth: 120 }}>{i18next.t('common.title')}</TableCell>
												<TableCell sx={{ ...adminStyles.tableHeaderCell, minWidth: 80 }}>{i18next.t('product.subject_code')}</TableCell>
												<TableCell sx={{ ...adminStyles.tableHeaderCell, minWidth: 70 }}>{i18next.t('product.price')}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{filteredProducts.length === 0 ? (
												<TableRow>
													<TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
														<Typography variant="body2" color="text.secondary">
															データがありません
														</Typography>
													</TableCell>
												</TableRow>
											) : (
												Object.entries(groupedProducts).map(([currName, currRows]) => {
													const isCollapsed = collapsedCurriculums.has(currName);
													const { allSelected: groupAllSelected, indeterminate: groupIndeterminate } = getCurriculumSelectState(currRows);
													return (
														<React.Fragment key={currName}>
															{/* カリキュラムグループヘッダー */}
															<TableRow sx={{ bgcolor: 'grey.100' }}>
																<TableCell colSpan={7} sx={{ py: 0.5, px: 1 }}>
																	<Box sx={{ display: 'flex', alignItems: 'center' }}>
																		<Checkbox
																			size="small"
																			checked={groupAllSelected}
																			indeterminate={groupIndeterminate}
																			onChange={() => handleCurriculumSelect(currRows)}
																			sx={{ p: '4px', mr: 0.5 }}
																		/>
																		<IconButton
																			size="small"
																			onClick={() => toggleCurriculumCollapse(currName)}
																			sx={{ p: '4px', mr: 0.5 }}
																		>
																			{isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
																		</IconButton>
																		<Typography variant="body2" sx={{ fontWeight: 600 }}>
																			{currName} ({currRows.length})
																		</Typography>
																	</Box>
																</TableCell>
															</TableRow>
															{/* カリキュラム内商品行 */}
															{!isCollapsed && currRows.map((product) => (
																<TableRow key={product.id} hover sx={{ '& .MuiTableCell-root': { py: '6px', px: '8px', fontSize } }}>
																	<TableCell padding="checkbox">
																		<Checkbox
																			size="small"
																			checked={formData.productIds.includes(product.id)}
																			onChange={() => handleProductToggle(product.id)}
																		/>
																	</TableCell>
																	<TableCell sx={{ whiteSpace: 'nowrap' }}>{product.category.name}</TableCell>
																	<TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.curriculumName}</TableCell>
																	<TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.volume}</TableCell>
																	<TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.title}</TableCell>
																	<TableCell sx={{ whiteSpace: 'nowrap' }}>{product.code}</TableCell>
																	<TableCell sx={{ whiteSpace: 'nowrap' }}>
																		{product.price
																			? i18next.t('common.price_yen', { price: product.price.toLocaleString() })
																			: "-"
																		}
																	</TableCell>
																</TableRow>
															))}
														</React.Fragment>
													);
												})
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</Paper>
						</Grid>
						<Grid item xs={12}>
							<Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
								<Button
									variant="outlined"
									onClick={() => navigate('/admin/product-sets')}
									sx={{ ...adminStyles.actionButton, fontSize }}
								>
									{i18next.t('common.cancel')}
								</Button>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									disabled={!canSave}
									sx={{ ...adminStyles.actionButton, fontSize }}
								>
									{i18next.t('common.save')}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Paper>
		</Container>
	);
};

export default ProductSetForm;